import * as Yup from "yup"

import useRouter from "@hooks/useRouter"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"

import { AlertBanner } from "@molecules"
import { EmailInput, PasswordInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"

import styles from "@pages/Auth/Auth.module.scss"
import useAuth from "@pages/Auth/Auth.store"
import { emailSchema } from "@pages/Auth/AuthEmailCodeForm/AuthEmailCodeForm"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import {
    AuthEmailPasswordErrorResponse,
    EmailAndPasswordSchema,
    UserNameAndPasswordPayload,
} from "./AuthEmailPasswordForm.types"

const emailAndPasswordSchema: Yup.ObjectSchema<EmailAndPasswordSchema> = Yup.object()
    .concat(emailSchema)
    .shape({
        password: Yup.string().required("Please enter a password"),
    })

export default function AuthEmailPasswordForm() {
    const { preferredAuthMethod, emailData, setEmailData, postAuthRedirectRoute } = useAuth()

    const router = useRouter()
    const { navigate } = useComponentNavigation()

    const isActive = preferredAuthMethod === "email_and_password"

    const onMutationSuccess = () => {
        router.replace(postAuthRedirectRoute)
    }

    const onRequestCodeInsteadClick = () => {
        navigate("email_and_code")
    }

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailData({
            email: event.target.value,
            emailId: null,
        })
    }

    return (
        <GenericForm<EmailAndPasswordSchema, User, AuthEmailPasswordErrorResponse, UserNameAndPasswordPayload>
            formConfig={{
                defaultValues: {
                    email_address: emailData?.email ? emailData?.email : "",
                    password: "",
                },
            }}
            formValidationSchema={emailAndPasswordSchema}
            mutationConfig={{
                method: "POST",
                endpoint: AUTH_ENDPOINTS.PASSWORD.EMAIL_AUTHENTICATE,
                genericErrorMessage: "Could not log in.",
            }}
            triggerFormReset={!isActive}
            onMutationSuccess={onMutationSuccess}
            className={styles.form}
        >
            {({ isPending, nonFieldError }) => (
                <>
                    {nonFieldError && (
                        <AlertBanner
                            type="banner"
                            message={nonFieldError}
                            status="error"
                            className={styles.topAlertBanner}
                        />
                    )}
                    <EmailInput<EmailAndPasswordSchema>
                        name="email_address"
                        label="Email address"
                        placeholder="Enter email address..."
                        isTabbable={isActive}
                        size="md"
                        onChangeCapture={onEmailChange}
                    />
                    <PasswordInput<EmailAndPasswordSchema>
                        name="password"
                        label="Password"
                        placeholder="Enter password..."
                        isTabbable={isActive}
                        size="md"
                        displayValidationMessages={false}
                    />
                    <div className={styles.formCTAs}>
                        <Button
                            type="submit"
                            size="lg"
                            variant="solid"
                            isFullWidth={true}
                            isTabbable={isActive}
                            isProcessing={isPending}
                            rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                        >
                            Sign In
                        </Button>
                        <Button
                            size="lg"
                            variant="ghost"
                            rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                            onClick={onRequestCodeInsteadClick}
                            isTabbable={isActive}
                            data-prevent-input-validation={true}
                            type="button"
                            isFullWidth={true}
                        >
                            Sign in with code
                        </Button>
                    </div>
                </>
            )}
        </GenericForm>
    )
}
